<template>
  <div class="pa-4">
    <v-card color="card" class="mb-6 elevation-2 rounded-lg">
      <v-sheet color="primary">
        <v-card-title class="pa-2">
          <div class="text-h6 ml-2 white--text">
            {{ getLabel('create_resource') }}
          </div>
        </v-card-title>
      </v-sheet>
      <v-card-text class="pa-0 pt-4">
        <v-alert v-if="errors.length" dense type="error" class="rounded-lg mx-3" dismissible @input="closeAlert">
          <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
        </v-alert>
        <v-form ref="form">
          <v-row>
            <v-col>
              <v-select
                v-model="type"
                class="pt-3 ml-4"
                :items="resourcesTypes"
                item-text="name"
                item-value="value"
                :label="getLabel('resource_type')"
              />
            </v-col>
            <v-col>
              <v-autocomplete
                v-model="selectedWorkspaces"
                :no-data-text="getLabel('table_no_data')"
                class="pt-1 mr-4"
                :items="workspaces"
                item-text="name"
                item-value="code"
                multiple
                chips
                dense
                :label="getLabel('workspaces')"
              />
            </v-col>
          </v-row>
        </v-form>
        <v-divider />
      </v-card-text>
      <v-card-actions class="pr-4 py-4">
        <v-spacer />
        <v-btn
          color="warning"
          dark
          @click="reset()"
        >
          <v-icon class="mr-2">mdi-broom</v-icon>

          {{ getLabel("clean") }}
        </v-btn>
        <v-btn
          class="ml-4"

          color="green darken-1"
          dark
          @click="saveResource()"
        >
          <v-icon class="mr-2">mdi-content-save</v-icon>

          {{ getLabel("save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-spacer />
    <!-- Table -->
    <v-card color="card" class="elevation-2 rounded-lg">
      <v-card-text class="pa-0">
        <v-data-table
          :headers="resourcesHeaders"
          :items="resources"
          hide-default-footer
          disable-sort
          class="elevation-1"
          :no-data-text="getLabel('table_no_data')"
        >
          <template #[`item.type`]="{ item }">
            {{ getResourceTypeLabel(item.type) }}
          </template>
          <template #[`item.workspace`]="{ item }">
            {{ getWorkspaceLabel(item.workspace) }}
          </template>
          <template #[`item.actions`]="{ index }">
            <v-btn icon @click="removeResource(index)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
        <v-divider />
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api';
import { useMutation } from '@vue/apollo-composable';

import { useMainStore } from '../../store/index';
import { resourcesTypes } from '@/utils/resourceTypes';
import { getGraphqlSchema } from '@/graphql/index';
import { EventBus, getLabel, getWorkspaceLabel, getResourceTypeLabel } from '@/utils';

export default {
  setup (props, { refs }) {
    const store = useMainStore();

    const errors = ref([]);

    const closeAlert = () => {
      errors.value = [];
    };

    const resourcesHeaders = [
      {
        text: getLabel('resource_type'),
        value: 'type',
        align: 'center'
      },
      {
        text: getLabel('workspace'),
        value: 'workspace',
        align: 'center'
      },
      {
        text: getLabel('actions'),
        value: 'actions',
        align: 'center'
      }
    ];

    const workspaces = store.workspaces.filter(w => w.project === 'alpha');

    const types = ref(['monitor', 'admin']);

    const resources = ref(store.collaborator.resources || []);

    const type = ref(null);

    const selectedWorkspaces = ref([]);

    const reset = () => {
      refs.form.reset();
    };

    // Update Collaborator Resource
    const resourcesToUpdate = ref(null);

    const { mutate: mutationUpdateCollaboratorResource, onError, onDone } = useMutation(
      getGraphqlSchema('updateCollaboratorResource'),
      () => ({
        variables:
          {
            uuid: store.collaborator.uuid,
            resources: resourcesToUpdate.value
          }
      })
    );

    onError(() => {
      store.loading = false;

      EventBus.$emit('show-notifications', [{
        message: getLabel('error_updating_resource'),
        type: 'danger'
      }]);
    });

    onDone(result => {
      store.collaborator.resources = result.data.updateCollaborator.resources;

      resourcesToUpdate.value = null;

      resources.value = result.data.updateCollaborator.resources;
      EventBus.$emit('show-notifications', [{
        message: getLabel('resource_updated'),
        type: 'success'
      }]);

      store.loading = false;
    });

    /** ******************************************* */

    const saveResource = () => {
      if (type.value !== null && selectedWorkspaces.value.length > 0) {
        store.loading = true;

        resourcesToUpdate.value = resources.value;

        selectedWorkspaces.value.forEach(w => {
          resourcesToUpdate.value.push({ type: type.value, workspace: w });
        });

        type.value = null;
        selectedWorkspaces.value = [];

        errors.value = [];

        mutationUpdateCollaboratorResource();

        return;
      }

      errors.value = [getLabel('check_fields')];
    };

    const removeResource = index => {
      resourcesToUpdate.value = resources.value;
      resourcesToUpdate.value.splice(index, 1);

      store.loading = true;

      mutationUpdateCollaboratorResource();
    };

    return {
      getLabel,
      getWorkspaceLabel,
      getResourceTypeLabel,
      errors,
      closeAlert,
      resourcesTypes,
      resourcesHeaders,
      workspaces,
      types,
      resources,
      type,
      selectedWorkspaces,
      reset,
      saveResource,
      removeResource
    };
  }
};
</script>

<style>
</style>
